/**
 * Native push registration for iOS + Android (Capacitor).
 *
 * On web builds, `Capacitor.isNativePlatform()` returns false and every call
 * here becomes a no-op — so this module is safe to import unconditionally.
 *
 * Flow:
 *   1. On app boot, call `initNativePush(navigate)`.
 *   2. Prompts the user for permission (iOS only — Android auto-grants pre-33).
 *   3. On `registration` event, we get an FCM token and POST it to the backend.
 *   4. On `pushNotificationActionPerformed`, we route to the URL in `notification.data.url`.
 *
 * Prerequisites:
 *   - Firebase project + APNs Key uploaded to Firebase Console.
 *   - `GoogleService-Info.plist` added to iOS/App/App/ in Xcode.
 *   - `google-services.json` added to android/app/ in Android Studio.
 *   - `@capacitor/push-notifications` installed.
 */
import { api } from "./api";

let _initialized = false;

async function _isNative() {
  try {
    // webpackIgnore skips static analysis; module is only present on native builds
    const { Capacitor } = await import(/* webpackIgnore: true */ "@capacitor/core");
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;  // Capacitor not installed on web builds
  }
}

async function _importPush() {
  try {
    const mod = await import(/* webpackIgnore: true */ "@capacitor/push-notifications");
    return mod.PushNotifications;
  } catch (err) {
    console.debug("[native-push] @capacitor/push-notifications not installed", err);
    return null;
  }
}

/**
 * Wire up native push. Pass in a `navigate` function from react-router so we
 * can deep-link when the user taps a notification.
 */
export async function initNativePush(navigate) {
  if (_initialized || !(await _isNative())) return;
  const PushNotifications = await _importPush();
  if (!PushNotifications) return;
  _initialized = true;

  const platform = await _getPlatform();

  // Request permission (iOS shows dialog; Android auto-grants pre-13)
  const permStatus = await PushNotifications.requestPermissions();
  if (permStatus.receive !== "granted") {
    console.debug("[native-push] permission not granted:", permStatus);
    return;
  }
  await PushNotifications.register();

  PushNotifications.addListener("registration", async (token) => {
    try {
      await api.post("/notifications/native/register", {
        token: token.value,
        platform,
      });
      console.debug("[native-push] registered", token.value.slice(0, 12) + "…", platform);
    } catch (err) {
      console.debug("[native-push] backend register failed", err);
    }
  });

  PushNotifications.addListener("registrationError", (err) => {
    console.debug("[native-push] registrationError", err);
  });

  // Foreground notification — up to you whether to show an in-app toast
  PushNotifications.addListener("pushNotificationReceived", (notification) => {
    console.debug("[native-push] received", notification);
  });

  // Background/foreground TAP → deep-link via router
  PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const url = action?.notification?.data?.url;
    if (url && typeof navigate === "function") navigate(url);
  });
}


/** Unregister — called from Notification Settings "Turn off native push". */
export async function unregisterNativePush() {
  if (!(await _isNative())) return;
  const PushNotifications = await _importPush();
  if (!PushNotifications) return;
  try {
    // FCM doesn't expose the token via getDeliveredNotifications, so we
    // rely on the server to purge invalid tokens on next send.
    await PushNotifications.removeAllListeners();
    _initialized = false;
    console.debug("[native-push] unregistered locally");
  } catch (err) {
    console.debug("[native-push] unregister failed", err);
  }
}


async function _getPlatform() {
  try {
    const { Capacitor } = await import(/* webpackIgnore: true */ "@capacitor/core");
    return Capacitor.getPlatform() === "ios" ? "ios" : "android";
  } catch { return "android"; }
}
